import type {
  Guide,
  GuideLegacyGalleryImage,
  GuideMaterial,
  GuideMaterialAsset,
  GuideMaterialMediaType,
} from "@/types";
import { getGuideVideoPoster } from "@/utils/guideVideo";

type UnknownRecord = Record<string, unknown>;

const hasOwn = (value: object, key: PropertyKey) =>
  Object.prototype.hasOwnProperty.call(value, key);

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const asString = (value: unknown, fallback = "") =>
  typeof value === "string" ? value : fallback;

const asNullableString = (value: unknown) =>
  typeof value === "string" && value.trim() ? value : null;

const asNullableNumber = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : null;

const asOrder = (value: unknown, fallback: number) =>
  typeof value === "number" && Number.isInteger(value) && value >= 0
    ? value
    : fallback;

const asId = (value: unknown, fallback: string): number | string =>
  (typeof value === "number" && Number.isFinite(value)) ||
  (typeof value === "string" && value.length > 0)
    ? value
    : fallback;

const fileNameFromUrl = (url: string) => {
  try {
    const pathname = new URL(url, "https://walkative.invalid").pathname;
    return decodeURIComponent(pathname.split("/").filter(Boolean).at(-1) ?? "");
  } catch {
    return "";
  }
};

const inferMimeType = (url: string, mediaType: GuideMaterialMediaType) => {
  const cleanUrl = url.split(/[?#]/, 1)[0].toLowerCase();
  const knownTypes: Record<string, string> = {
    ".avif": "image/avif",
    ".gif": "image/gif",
    ".jpeg": "image/jpeg",
    ".jpg": "image/jpeg",
    ".png": "image/png",
    ".svg": "image/svg+xml",
    ".webp": "image/webp",
    ".m4v": "video/mp4",
    ".mov": "video/quicktime",
    ".mp4": "video/mp4",
    ".ogg": "video/ogg",
    ".webm": "video/webm",
    ".pdf": "application/pdf",
  };
  const extension = Object.keys(knownTypes).find((item) =>
    cleanUrl.endsWith(item),
  );
  return (
    (extension ? knownTypes[extension] : undefined) ??
    (mediaType === "image"
      ? "image/*"
      : mediaType === "video"
        ? "video/*"
        : "application/octet-stream")
  );
};

const inferMediaType = (
  value: unknown,
  mimeType: string,
  url: string,
): GuideMaterialMediaType => {
  if (value === "image" || value === "video" || value === "document") {
    return value;
  }
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  if (/\.(?:mp4|webm|ogg|mov|m4v)(?:[?#].*)?$/i.test(url)) return "video";
  if (/\.(?:avif|gif|jpe?g|png|svg|webp)(?:[?#].*)?$/i.test(url)) {
    return "image";
  }
  return "document";
};

const normalizeAsset = (
  value: unknown,
  mediaTypeValue: unknown,
): { asset: GuideMaterialAsset; mediaType: GuideMaterialMediaType } | null => {
  if (!isRecord(value)) return null;

  const url = asString(value.url).trim();
  if (!url) return null;

  const suppliedMimeType = asString(value.mime_type).trim();
  const mediaType = inferMediaType(mediaTypeValue, suppliedMimeType, url);
  const mimeType = suppliedMimeType || inferMimeType(url, mediaType);

  return {
    mediaType,
    asset: {
      url,
      preview_url: asNullableString(value.preview_url),
      download_url: asNullableString(value.download_url),
      mime_type: mimeType,
      file_name:
        asString(value.file_name).trim() || fileNameFromUrl(url) || "material",
      file_size_bytes: asNullableNumber(value.file_size_bytes),
      width: asNullableNumber(value.width),
      height: asNullableNumber(value.height),
      duration_seconds: asNullableNumber(value.duration_seconds),
    },
  };
};

const normalizeV2Material = (
  value: unknown,
  index: number,
): GuideMaterial | null => {
  if (!isRecord(value)) return null;
  if (
    typeof value.product_id !== "number" ||
    !Number.isFinite(value.product_id)
  ) {
    return null;
  }

  const normalizedAsset = normalizeAsset(value.asset, value.media_type);
  if (!normalizedAsset) return null;
  const order = asOrder(value.order, index);

  return {
    id: asId(value.id, `material:${value.product_id}:${order}:${index}`),
    source_id: asNullableString(value.source_id),
    product_id: value.product_id,
    product_title: asNullableString(value.product_title),
    media_type: normalizedAsset.mediaType,
    youtube_url: asString(value.youtube_url),
    order,
    title: asNullableString(value.title),
    caption: asString(value.caption),
    alt: asString(value.alt),
    asset: normalizedAsset.asset,
  };
};

const normalizeLegacyMaterial = (
  value: GuideLegacyGalleryImage,
  index: number,
): GuideMaterial | null => {
  const url = asString(value.video || value.image).trim();
  if (!url || typeof value.product_id !== "number") return null;

  const requestedType = value.video ? "video" : value.media_type;
  const mediaType = inferMediaType(requestedType, "", url);
  const order = asOrder(value.order, index);

  return {
    id: asId(value.id, `legacy:${value.product_id}:${order}:${index}`),
    source_id: null,
    product_id: value.product_id,
    media_type: mediaType,
    youtube_url: "",
    order,
    title: null,
    caption: asString(value.caption),
    alt: asString(value.alt),
    asset: {
      url,
      preview_url: getGuideVideoPoster(value) ?? null,
      download_url: null,
      mime_type: inferMimeType(url, mediaType),
      file_name: fileNameFromUrl(url) || "material",
      file_size_bytes: null,
      width: null,
      height: null,
      duration_seconds: null,
    },
  };
};

export const normalizeGuideMaterials = (guide: Guide): GuideMaterial[] => {
  const hasV2Materials = hasOwn(guide, "tour_materials");
  if (hasV2Materials) {
    const materials = Array.isArray(guide.tour_materials)
      ? guide.tour_materials
      : [];
    return materials
      .map((material, index) => normalizeV2Material(material, index))
      .filter((material): material is GuideMaterial => material !== null);
  }

  return (guide.gallery_images ?? [])
    .map((material, index) => normalizeLegacyMaterial(material, index))
    .filter((material): material is GuideMaterial => material !== null);
};

export const normalizeGuideBio = (guide: Guide): Guide => {
  if (!hasOwn(guide, "tour_materials") && !hasOwn(guide, "gallery_images")) {
    return guide;
  }

  return {
    ...guide,
    tour_materials: normalizeGuideMaterials(guide),
  };
};
